import type { Dispatch } from '@reduxjs/toolkit';
import type {
  ICompanyUserState,
  INewAndUpdateUserDto,
} from 'src/legacy/@types/user';

import { createSlice } from '@reduxjs/toolkit';
import { setSession } from 'src/legacy/auth/utils';
import axios from 'src/legacy/utils/axios';

// ----------------------------------------------------------------------

const initialState: ICompanyUserState = {
  actionStatus: {
    create: 'none',
    delete: 'none',
    update: 'none',
  },
  companyUsers: null,
  error: null,
  isLoading: false,
  selectedUser: null,
};

const slice = createSlice({
  initialState,
  name: 'user',
  reducers: {
    // CLEAR ERROR
    clearError(state) {
      state.error = '';
    },

    // CREATE NEW COMPANY USER
    createCompanyUserSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        create: 'complete',
      };
      state.error = null;
      state.companyUsers = [...(state.companyUsers || []), action.payload];
    },

    // DELETE COMPANY USER
    deleteCompanyUserSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        delete: 'complete',
      };
      if (state.companyUsers) {
        state.companyUsers = state.companyUsers.filter(
          (user) => !action.payload.userIds.includes(user.id)
        );
      }
    },

    // GET COMPANY USERS
    getCompanyUsersSuccess(state, action) {
      state.isLoading = false;
      state.companyUsers = action.payload;
    },

    // GET COMPANY USER
    getCompanyUserSuccess(state, action) {
      state.isLoading = false;
      state.selectedUser = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // UPDATE ACTION STATUS
    updateActionState(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        ...action.payload,
      };
    },

    // UPDATE COMPANY USER
    updateCompanyUserSuccess(state, action) {
      state.actionStatus = {
        ...state.actionStatus,
        update: 'complete',
      };
      state.error = null;
      if (state.companyUsers) {
        state.companyUsers = state.companyUsers.map((user) => {
          if (user.id === action.payload.id) {
            return action.payload;
          }
          return user;
        });
      }
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { clearError, updateActionState } = slice.actions;

// ----------------------------------------------------------------------

export const getCompanyUsers = (companyId: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/users/`
      );
      let allResults: any[] = response.data.results;

      if (
        response.data.count > allResults.length ||
        response.data.next !== ''
      ) {
        await Promise.all(
          Array(Math.ceil(response.data.count / 20) - 1)
            .fill(0)
            .map(async (_, index) => {
              const response2 = await axios.get(
                `/organization/companies/${companyId}/users/`,
                {
                  params: { page: index + 2 },
                }
              );
              allResults = [...allResults, ...response2.data.results];
            })
        );
      }

      dispatch(slice.actions.getCompanyUsersSuccess(allResults));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const createCompanyUser = (
  companyId: string,
  newUser: INewAndUpdateUserDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateActionState({ create: 'loading', error: null })
    );
    try {
      const response = await axios.post(
        `/organization/companies/${companyId}/users/`,
        newUser
      );
      dispatch(slice.actions.createCompanyUserSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ create: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const getCompanyUser = (companyId: string, userId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `/organization/companies/${companyId}/users/${userId}/`
      );
      dispatch(slice.actions.getCompanyUserSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const updateCompanyUser = (
  companyId: string,
  userId: number,
  updatedUser: INewAndUpdateUserDto
) => {
  return async (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateActionState({ error: null, update: 'loading' })
    );
    try {
      const response = await axios.put(
        `/organization/companies/${companyId}/users/${userId}/`,
        updatedUser
      );
      dispatch(slice.actions.updateCompanyUserSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ update: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const deleteCompanyUser = (companyId: string, userIds: number[]) => {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.updateActionState({ delete: 'loading' }));
    try {
      await Promise.all(
        userIds.map(async (id) => {
          await axios.delete(
            `/organization/companies/${companyId}/users/${id}/`
          );
        })
      );
      dispatch(slice.actions.deleteCompanyUserSuccess({ userIds }));
    } catch (error) {
      dispatch(slice.actions.updateActionState({ delete: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};

// ----------------------------------------------------------------------

export const changeCurrentUserPassword = (
  newPassword: string,
  confirmPassword: string
) => {
  return async (dispatch: Dispatch) => {
    dispatch(
      slice.actions.updateActionState({ error: null, update: 'loading' })
    );
    try {
      const response = await axios.put(`/core/current-user/change-password/`, {
        password: newPassword,
        password2: confirmPassword,
      });
      const { access, refresh } = response.data;
      setSession(access, refresh);
      dispatch(
        slice.actions.updateActionState({ error: null, update: 'complete' })
      );
    } catch (error) {
      dispatch(slice.actions.updateActionState({ update: 'failed' }));
      dispatch(slice.actions.hasError(error));
    }
  };
};
